/**
 * Un número tal como una persona lo escribe en Chile, o `null`.
 *
 * POR QUÉ ESTO NO ES `Number(texto)`
 *
 * Acá los miles van con punto y los decimales con coma: 150.000.000 y 4,68.
 * `Number("150.000.000")` da `NaN` y `Number("4,68")` también, así que sin
 * traducir, todo campo numérico del modal se vacía solo al guardar.
 *
 * Y traducir mal es peor que no traducir. Si el punto de miles se lee como
 * decimal, 150.000.000 se convierte en 150: el campo se ve perfectamente
 * escrito, la fila queda perfectamente formada, y el comparable entra a un
 * informe pericial valiendo la millonésima parte. Por eso esto vive aparte, con
 * tests, y por eso el modal MUESTRA cómo interpretó lo que se escribió — la
 * única ambigüedad real que queda (un punto solo) se resuelve a la vista de la
 * persona en vez de a sus espaldas.
 *
 * Las reglas, en orden:
 *
 *   1. Con COMA, la última coma es el decimal y todo punto es de miles. Es la
 *      escritura local y no tiene ninguna otra lectura.
 *   2. Sin coma y con MÁS DE UN PUNTO, todos son de miles: no hay número con
 *      dos separadores decimales.
 *   3. Sin coma y con UN punto seguido de EXACTAMENTE TRES dígitos, es de
 *      miles: "1.234" acá se lee mil doscientos treinta y cuatro.
 *   4. Sin coma y con un punto en cualquier otra posición, es decimal: quien
 *      copia de una pantalla en inglés trae "4.68", y no existe grupo de miles
 *      de dos cifras.
 */
export function numeroEscrito(texto: string): number | null {
  const limpio = texto.trim();

  if (limpio === "") return null;

  // Solo dígitos, separadores y un signo al principio. Cualquier otra cosa es
  // basura, y `parseFloat` la convertiría igual en una cifra creíble
  // ("4abc" → 4).
  if (!/^-?[\d.,]+$/.test(limpio)) return null;

  const comas = (limpio.match(/,/g) ?? []).length;

  if (comas > 1) return null;

  const normalizado =
    comas === 1
      ? limpio.replace(/\./g, "").replace(",", ".")
      : sinComa(limpio);

  if (normalizado === null) return null;

  const valor = Number(normalizado);

  return Number.isFinite(valor) ? valor : null;
}

/**
 * El texto sin comas, con los puntos resueltos como miles o como decimal.
 *
 * Devuelve `null` cuando los puntos no forman un número posible —"1.2.3"—, en
 * vez de una cifra inventada a partir de lo que se entendió.
 */
function sinComa(texto: string): string | null {
  const puntos = (texto.match(/\./g) ?? []).length;

  if (puntos === 0) return texto;

  const ultimo = texto.slice(texto.lastIndexOf(".") + 1);

  // Más de un punto, o un punto con exactamente tres dígitos detrás: miles. En
  // los dos casos los grupos tienen que ser de tres, o el número está mal
  // escrito y no hay nada que adivinar.
  if (puntos > 1 || ultimo.length === 3) {
    if (!/^-?\d{1,3}(\.\d{3})+$/.test(texto)) return null;

    return texto.replace(/\./g, "");
  }

  return texto;
}

/**
 * `round($x, 2)` de PHP: mitades hacia afuera del cero, simétrico en negativos.
 *
 * Lo usan los gemelos de los totales que calcula el servidor (construcciones y
 * maquinaria): cada paso se redondea como lo hace PHP, no al final.
 */
export function aDosDecimales(valor: number): number {
  return (Math.sign(valor) * Math.round(Math.abs(valor) * 100 + 1e-9)) / 100;
}
